import React from "react";

import type { PlanNamesType } from "@hz/stealthy-gmc-cloaker-common/constants";
import { getPlanDisplayName, isOveragePlan } from "@hz/stealthy-gmc-cloaker-common/constants";

import { Info, Zap } from "lucide-react";
import { useSelector } from "react-redux";

import { Box, Button, Flex, HStack, Progress, Text, Tooltip, VStack } from "@chakra-ui/react";

import { selectUserState } from "@state/userSlice/userSlice";

import { formatUnixDate } from "@utils/formatDate";

type QuotaUsageProps = {
  current: number;
  limit: number;
  onUpgrade?: () => void;
  size?: "sm" | "md" | "lg";
  currentPlan: PlanNamesType;
};
export const QuotaUsageBar = ({ current, limit, onUpgrade, size = "md", currentPlan }: QuotaUsageProps) => {
  const percentage = Math.min((current / limit) * 100, 100);
  const isNearLimit = percentage >= 80;
  const isAtLimit = current >= limit;
  // TITAN and C4_5M bill overage past their limit instead of blocking.
  const hasOverageBilling = isOveragePlan(currentPlan);
  const isOverage = hasOverageBilling && isAtLimit;
  const overageSessions = isOverage ? current - limit : 0;
  const overageCost = Math.floor(overageSessions / 100_000) * 300;
  const formatNumber = (num: number) => num.toLocaleString();
  const { billing } = useSelector(selectUserState);

  const sizes = {
    sm: {
      fontSize: "xs",
      progressHeight: 2,
      buttonSize: "xs",
      iconSize: 12,
      spacing: 1,
    },
    md: {
      fontSize: "sm",
      progressHeight: 3,
      buttonSize: "sm",
      iconSize: 14,
      spacing: 2,
    },
    lg: {
      fontSize: "md",
      progressHeight: 4,
      buttonSize: "md",
      iconSize: 16,
      spacing: 3,
    },
  };

  // const QuotaDateRange = () => {

  //   return (
  //     <p style={{ color: 'gray' }}>
  //       Usage reset period: {formatDate(start)} – {formatDate(end)}
  //     </p>
  //   );
  // };

  // Safe usage
  // const startDate = formatUnixDate(billing?.stripeCurrentPeriodStart);
  // const endDate = formatUnixDate(billing?.stripeCurrentPeriodEnd);

  return (
    <Box
      bg="gray.800"
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor="gray.700"
      mb={6}
    >
      <Flex
        justify="space-between"
        align="center"
        mb={4}
      >
        <HStack spacing={2}>
          <Text
            fontSize="lg"
            fontWeight="medium"
            color="white"
          >
            Total monthly sessions for {billing?.yearMonth}
          </Text>
          <Tooltip
            label="Monthly sessions for current month. Resets every month."
            placement="top"
          >
            <Box
              as={Info}
              size={16}
              color="gray.400"
              _hover={{ color: "white" }}
              cursor="help"
            />
          </Tooltip>
        </HStack>
        <Text
          fontSize="sm"
          color="gray.400"
        >
          Quota resets 1st of every month.
        </Text>

        {/* {
          startDate && endDate && (
            <Text fontSize="sm" color="gray.400">
            {startDate} – {endDate}
          </Text>
          )
        } */}
      </Flex>

      <VStack
        spacing={sizes[size].spacing}
        align="stretch"
        width="full"
      >
        <Tooltip
          label={isNearLimit ? "You're approaching your quota limit" : undefined}
          isDisabled={!isNearLimit}
        >
          <Box width="full">
            <Progress
              value={percentage}
              size="sm"
              height={sizes[size].progressHeight}
              borderRadius="full"
              bg="gray.700"
              aria-label="Quota usage progress"
              sx={{
                "& > div": {
                  background: isNearLimit
                    ? "linear-gradient(90deg, #ff3d00 0%, #ff5722 100%)"
                    : "linear-gradient(90deg, #48BB78 0%, #38A169 100%)",
                  transition: "all 0.2s ease-in-out",
                },
              }}
            />
          </Box>
        </Tooltip>
        <Flex
          justify="space-between"
          align="center"
        >
          <Text
            color="gray.400"
            fontSize={sizes[size].fontSize}
          >
            Your Plan: {getPlanDisplayName(currentPlan)}. {formatNumber(current)} / {formatNumber(limit)}{" "}
            sessions.
            {isOverage && (
              <Text
                as="span"
                color="#ff3d00"
                ml={2}
              >
                ({formatNumber(overageSessions)} overage — ${overageCost.toLocaleString()} billed at
                $300/100k)
              </Text>
            )}
          </Text>
          {/* Overage plans are top-tier — there is nothing to upgrade to. */}
          {onUpgrade && !hasOverageBilling && (
            <Button
              size={sizes[size].buttonSize}
              variant="ghost"
              color="#ff3d00"
              leftIcon={<Zap size={sizes[size].iconSize} />}
              onClick={onUpgrade}
              _hover={{
                bg: "whiteAlpha.50",
              }}
            >
              Upgrade Plan
            </Button>
          )}
        </Flex>
      </VStack>
    </Box>
  );
};
